// =============================================================================
// faceBands — the decision bands and liveness tuning of the face stack, in ONE
// place with no model imports, so every surface that quotes a threshold reads
// the same number.
//
// Before 2026-09-18 these lived in six copies (audit 2026-09-15 §4 #9): the
// canonical values in faceRecognition.ts (which pulls MediaPipe + ONNX at
// import time, so light surfaces would not import it), three literals in the
// calibration panel, a colour rule in the match log that painted the whole
// legitimate confirm band red (≥0.6 green / ≥0.5 amber / else rose in
// confidence space, while the tap-confirm band runs 0.393–0.5), the liveness
// tuning hand-copied between the kiosk and the piece-work modal with a "keep
// in sync" comment, and 1 − d/1.4 re-implemented in the edge function. The
// server checks none of them, so a drifted copy is invisible until someone
// reads a wrong colour as a wrong clock.
//
// faceRecognition.ts re-exports the names it used to define, so nothing that
// imported from there changes. The edge function keeps its own copy of the
// SCALE (it cannot import src/), pinned equal by faceBands.mirror.test.ts.
// =============================================================================

// ---- distance bands (Euclidean L2 on L2-normalised SFace vectors) -----------
/** Nothing past this is ever offered — the measured stranger floor is ~0.9. */
export const MATCH_THRESHOLD       = 0.8
/** Two enrolled workers closer than this to the probe → AMBIGUOUS, never a pick. */
export const AMBIGUITY_GAP         = 0.05
/** At or under this a match clocks hands-free (the original calibrated 0.7). */
export const AUTO_ACCEPT_THRESHOLD = 0.7
/** (AUTO_ACCEPT_THRESHOLD, CONFIRM_MAX_DISTANCE] is the tap-to-confirm band. */
export const CONFIRM_MAX_DISTANCE  = 0.85

// ---- confident-skip (the kiosk's fast-clock rule; CONFIDENT liveness mode) --
export const FAST_CLOCK_MAX_DISTANCE = 0.60   // tighter than AUTO_ACCEPT_THRESHOLD
export const FAST_CLOCK_MIN_GAP      = 0.10   // clear separation from the runner-up

// ---- confidence, the DISPLAY unit -------------------------------------------
/** L2-normalised vectors → Euclidean lives in [0, ~1.4]; confidence = 1 − d/1.4.
 *  Audit/display only — matching uses the raw distance. The edge function
 *  carries the same constant in _shared/faceBands.ts. */
export const DISTANCE_SCALE = 1.4

export function distanceToConfidence(d: number): number {
  return Math.max(0, Math.min(1, 1 - d / DISTANCE_SCALE))
}
export function confidenceToDistance(c: number): number {
  return (1 - Math.max(0, Math.min(1, c))) * DISTANCE_SCALE
}

// ---- which band a decision landed in ------------------------------------------
export type FaceBand = 'auto' | 'confirm' | 'reject'

export function bandOfDistance(d: number | null | undefined): FaceBand | null {
  if (d == null || !Number.isFinite(d)) return null
  if (d <= AUTO_ACCEPT_THRESHOLD) return 'auto'
  if (d <= CONFIRM_MAX_DISTANCE)  return 'confirm'
  return 'reject'
}
/** The match log stores confidence, not distance — same bands, other unit. */
export function bandOfConfidence(c: number | null | undefined): FaceBand | null {
  if (c == null || !Number.isFinite(c)) return null
  return bandOfDistance(confidenceToDistance(c))
}

/** Tailwind text class per band — green = clocked hands-free, amber = clocked
 *  by tap (legitimate, not a warning), rose = would not clock. */
export const BAND_TEXT_CLASS: Record<FaceBand, string> = {
  auto:    'text-emerald-300',
  confirm: 'text-amber-300',
  reject:  'text-rose-300',
}

// ---- liveness challenge + scan-loop tuning (kiosk AND piece-work modal) ------
// Values shared by ClockFace.tsx and piecework/faceScan.tsx. A change here
// retunes both; that is the point.
export const LIVENESS_TUNING = {
  /** eye-closure blendshape at which the eyes count as shut */
  BLINK_HIGH:   0.5,
  /** …and as open again */
  BLINK_LOW:    0.2,
  /** signed-yaw change from the baseline that counts as a real turn */
  TURN_DELTA:   0.30,
  /** window to pass the challenge before LIVENESS_FAIL / reset */
  CHALLENGE_MS: 7000,
} as const

export const SCAN_TUNING = {
  /** same-worker good frames before an AUTO candidate is locked */
  CONSENSUS_FRAMES:    2,
  /** good frames inside the confirm band before tap-confirm is offered */
  CONFIRM_BAND_FRAMES: 3,
} as const
